import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Sparkles, Send, Loader2, RefreshCw, AlertTriangle, TrendingUp, Route, PackageSearch, IndianRupee, ShieldCheck, Mic, Square, Volume2 } from 'lucide-react';
import { apiGet, apiPost, apiPostForm } from '../../lib/api';
import { getIndustryTemplate } from '../../lib/industryTemplates';
import { useAuth } from '../../context/AuthContext';

const tabs = [
  ['assistant', 'AI Assistant'],
  ['summary', 'Summary'],
  ['forecast', 'Forecast'],
  ['billing', 'Billing Audit'],
  ['payments', 'Payments'],
  ['customers', 'Customers'],
  ['routes', 'Routes'],
  ['inventory', 'Inventory'],
  ['audit', 'Audit'],
];

const Panel = ({ title, children, icon: Icon = Sparkles }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-soft p-5">
    <div className="flex items-center gap-2 text-rozops-accent text-sm font-bold mb-4">
      <Icon className="w-4 h-4" /> {title}
    </div>
    {children}
  </div>
);

const Empty = ({ text }) => <div className="text-sm text-rozops-text-muted py-6 text-center">{text}</div>;

const audioDataUrl = (base64, mime = 'audio/mpeg') => base64 ? `data:${mime};base64,${base64}` : null;
const todayIso = () => new Date().toISOString().slice(0, 10);
const dateKey = (value) => value ? new Date(value).toDateString() : '';
const dateLabel = (value) => value ? new Date(value).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : '';

const Risk = ({ value }) => {
  const cls = value === 'high' ? 'bg-red-50 text-red-600' : value === 'medium' ? 'bg-amber-50 text-amber-600' : value === 'low' ? 'bg-rozops-green-light text-rozops-green' : 'bg-gray-50 text-rozops-text-muted';
  return <span className={`rounded-full px-2 py-1 text-[11px] font-bold uppercase ${cls}`}>{value || 'none'}</span>;
};

const ChatDataPreview = ({ payload }) => {
  if (payload?.intent === 'check_tomorrow_requirement' && payload?.data?.totals?.length) {
    return (
      <div className="mt-3 pt-3 border-t border-gray-200/60 space-y-1">
        {payload.data.totals.map(item => (
          <div key={item.product} className="flex justify-between gap-4 text-xs">
            <span>{item.product}</span>
            <b>{item.quantity}</b>
          </div>
        ))}
      </div>
    );
  }
  if (payload?.intent === 'check_pending_payments' && payload?.data?.customers?.length) {
    return (
      <div className="mt-3 pt-3 border-t border-gray-200/60 space-y-1">
        {payload.data.customers.slice(0, 5).map(row => (
          <div key={row.invoice_id} className="flex justify-between gap-4 text-xs">
            <span>{row.customer || row.invoice_number}</span>
            <b>₹{row.due_amount?.toLocaleString('en-IN')}</b>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const aiTextFromHistory = (row, output) => (
  output?.confirmation_message
  || output?.summary
  || row.action_taken
  || 'Interpreted.'
);

const inputModeFromHistory = (row, output) => output?.input_mode || row.extracted_data?._input_mode || 'text';

const historyToMessages = (rows = []) => {
  const ordered = [...rows].reverse();
  return ordered.flatMap(row => {
    const output = row.output_data || {};
    const mode = inputModeFromHistory(row, output);
    const voice = mode === 'voice';
    const createdAt = row.created_at ? new Date(row.created_at).toLocaleString() : '';
    const payload = {
      ...output,
      request_id: row.id,
      intent: output.intent || row.detected_intent,
      action_key: output.action_key || row.action_key,
      entities: output.entities || row.extracted_data || {},
      requires_confirmation: row.status === 'pending_confirmation' && Boolean(output.requires_confirmation),
      history_status: row.status,
    };
    return [
      {
        who: 'user',
        text: row.input_text || (voice ? 'Voice message' : 'Message'),
        voice,
        history: true,
        timestamp: createdAt,
      },
      {
        who: 'ai',
        text: aiTextFromHistory(row, output),
        payload,
        voice,
        history: true,
        timestamp: createdAt,
        canConfirm: row.status === 'pending_confirmation',
      },
    ];
  });
};

export default function AIAssistant() {
  const [text, setText] = useState('');
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [activeTab, setActiveTab] = useState('assistant');
  const [insights, setInsights] = useState({});
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyDate, setHistoryDate] = useState(todayIso());
  const [voiceState, setVoiceState] = useState('idle');
  const [voiceError, setVoiceError] = useState('');
  const chatScrollRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const cancelRecordingRef = useRef(false);
  const { business } = useAuth();
  const template = getIndustryTemplate(business?.business_type);
  const voiceBusy = voiceState === 'recording' || voiceState === 'processing';

  const loadInsights = useCallback(async () => {
    setLoadingInsights(true);
    try {
      const [summary, forecast, billing, payments, customers, routes, inventory, history, auditLog] = await Promise.all([
        apiGet('/ai/dashboard'),
        apiGet('/ai/demand-forecast?days=7'),
        apiGet('/ai/billing-audit'),
        apiGet('/ai/payment-insights'),
        apiGet('/ai/customer-insights'),
        apiGet('/ai/route-insights'),
        apiGet('/ai/inventory-insights'),
        apiGet(`/ai/history?history_date=${historyDate}`),
        apiGet('/ai/audit-log'),
      ]);
      setInsights({
        summary: summary.data,
        forecast: forecast.data,
        billing: billing.data,
        payments: payments.data,
        customers: customers.data,
        routes: routes.data,
        inventory: inventory.data,
        history: history.data || [],
        auditLog: auditLog.data || [],
      });
      setMessages(current => current.length ? current : historyToMessages(history.data || []));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoadingInsights(false);
    }
  }, [historyDate]);

  useEffect(() => {
    loadInsights();
  }, [loadInsights]);

  useEffect(() => {
    const node = chatScrollRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [messages, busy, voiceState]);

  const loadChatHistory = async (targetDate = historyDate) => {
    setLoadingHistory(true);
    try {
      const query = targetDate ? `?history_date=${targetDate}` : '';
      const res = await apiGet(`/ai/history${query}`);
      const history = res.data || [];
      setInsights(current => ({ ...current, history }));
      setMessages(historyToMessages(history));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoadingHistory(false);
    }
  };

  const changeHistoryDate = async (value) => {
    setHistoryDate(value);
    await loadChatHistory(value);
  };

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    cancelRecordingRef.current = true;
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
    streamRef.current?.getTracks?.().forEach(track => track.stop());
  }, []);

  const send = async (e) => {
    e?.preventDefault();
    if (!text.trim() || voiceBusy) return;
    const now = new Date().toISOString();
    const userMsg = { who: 'user', text: text.trim(), timestamp: now };
    setMessages(m => [...m, userMsg]);
    const q = text.trim();
    setText('');
    setBusy(true);
    try {
      const r = await apiPost('/ai/interpret', { text: q, context_role: 'merchant' });
      const ai = r.data;
      setMessages(m => [...m, { who: 'ai', text: ai.confirmation_message || 'Interpreted.', payload: ai, timestamp: new Date().toISOString() }]);
      loadInsights();
    } catch (err) {
      toast.error(err.message);
      setMessages(m => [...m, { who: 'ai', text: 'Sorry — I could not process that.' }]);
    } finally { setBusy(false); }
  };

  const apply = async (m) => {
    try {
      const r = await apiPost('/ai/confirm-action', { request_id: m.payload.request_id, intent: m.payload.intent, action_key: m.payload.action_key, entities: m.payload.entities || {} });
      toast.success(r.data.message);
      setMessages(msgs => [...msgs, { who: 'ai', text: r.data.message }]);
      loadInsights();
    } catch (err) { toast.error(err.message); }
  };

  const playVoice = async (url) => {
    if (!url) return;
    setVoiceState('playing');
    try {
      const audio = new Audio(url);
      audio.onended = () => setVoiceState('idle');
      audio.onerror = () => setVoiceState('idle');
      await audio.play();
    } catch {
      setVoiceState('idle');
      toast.error('Tap replay to hear the AI answer.');
    }
  };

  const sendVoice = async (blob) => {
    if (!blob || blob.size === 0) {
      setVoiceState('idle');
      return toast.error('No voice recorded');
    }
    if (blob.size > 15 * 1024 * 1024) {
      setVoiceState('idle');
      return toast.error('Voice note is too large. Please keep it under 60 seconds.');
    }
    setVoiceState('processing');
    setVoiceError('');
    try {
      const form = new FormData();
      form.append('audio', blob, `rozops-voice-${Date.now()}.webm`);
      form.append('context_role', 'merchant');
      const res = await apiPostForm('/ai/voice/interpret', form, { timeoutMs: 90000 });
      const transcript = res.data?.transcript || '';
      const ai = res.data?.ai || {};
      const url = audioDataUrl(res.data?.audio_base64, res.data?.audio_mime);
      const now = new Date().toISOString();
      setMessages(m => [
        ...m,
        { who: 'user', text: transcript || 'Voice message', voice: true, timestamp: now },
        { who: 'ai', text: ai.confirmation_message || 'Interpreted.', payload: ai, audioUrl: url, voice: true, timestamp: new Date().toISOString() },
      ]);
      await loadInsights();
      if (url) await playVoice(url);
      else setVoiceState('idle');
    } catch (err) {
      setVoiceState('idle');
      setVoiceError(err.message);
      toast.error(err.message);
    }
  };

  const stopRecording = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (recorderRef.current?.state === 'recording') {
      recorderRef.current.stop();
    }
  };

  const startRecording = async () => {
    if (busy || voiceBusy) return;
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      setVoiceError('Voice recording is not supported in this browser.');
      return;
    }
    setVoiceError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const preferred = 'audio/webm;codecs=opus';
      const mimeType = MediaRecorder.isTypeSupported(preferred) ? preferred : 'audio/webm';
      const recorder = new MediaRecorder(stream, { mimeType });
      recorderRef.current = recorder;
      recorder.ondataavailable = (event) => {
        if (event.data?.size) chunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        stream.getTracks().forEach(track => track.stop());
        streamRef.current = null;
        if (cancelRecordingRef.current) return;
        const blob = new Blob(chunksRef.current, { type: mimeType });
        sendVoice(blob);
      };
      cancelRecordingRef.current = false;
      recorder.start();
      setVoiceState('recording');
      timerRef.current = setTimeout(() => {
        toast.info('Recording stopped after 60 seconds.');
        stopRecording();
      }, 60000);
    } catch (err) {
      setVoiceState('idle');
      setVoiceError('Microphone permission was blocked or unavailable.');
      toast.error('Microphone permission was blocked or unavailable.');
    }
  };

  const examples = template.aiExamples;

  return (
    <div className="space-y-5" data-testid="ai-page">
      <div className="sticky top-14 z-30 -mt-4 -mx-4 sm:-mt-6 sm:-mx-6 lg:-mt-8 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-2 bg-rozops-bg/95 backdrop-blur border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {tabs.map(([key, label]) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`rounded-xl px-3 py-2 text-sm font-bold transition-all ${activeTab === key ? 'bg-rozops-blue text-white shadow-soft' : 'bg-white border border-gray-100 text-rozops-text-muted hover:text-rozops-blue'}`}
            >
              {label}
            </button>
          ))}
        </div>
        <button onClick={loadInsights} disabled={loadingInsights} className="rounded-xl bg-white border border-gray-100 px-3 py-2 text-sm font-bold text-rozops-text-muted hover:text-rozops-blue inline-flex items-center gap-2">
          <RefreshCw className={`w-4 h-4 ${loadingInsights ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {activeTab === 'assistant' && (
      <>
      <div className="bg-white rounded-2xl border border-gray-100 shadow-soft p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-rozops-text-muted">Assistant chat</div>
            <div className="text-xs text-rozops-text-muted">Text and voice history for {dateLabel(historyDate)}.</div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="date"
              value={historyDate}
              onChange={(e) => changeHistoryDate(e.target.value)}
              className="rounded-xl border border-gray-100 px-3 py-2 text-xs font-bold text-rozops-text-muted focus:outline-none focus:ring-2 focus:ring-rozops-blue/20 focus:border-rozops-blue"
              data-testid="ai-history-date"
            />
            <button type="button" onClick={() => loadChatHistory()} disabled={loadingHistory || busy || voiceBusy} className="rounded-xl border border-gray-100 px-3 py-2 text-xs font-bold text-rozops-text-muted hover:text-rozops-blue inline-flex items-center gap-2 disabled:opacity-50">
              <RefreshCw className={`w-3.5 h-3.5 ${loadingHistory ? 'animate-spin' : ''}`} /> History
            </button>
          </div>
        </div>
        <div ref={chatScrollRef} className="mt-4 h-[60vh] overflow-y-auto pr-1 space-y-3" data-testid="ai-chat-scroll">
          {messages.length === 0 && (
            <div className="space-y-2">
              <div className="text-xs uppercase tracking-widest font-bold text-rozops-text-muted">Try one of these</div>
              <div className="flex flex-wrap gap-2">
                {examples.map(e => (
                  <button key={e} onClick={() => setText(e)} className="rounded-full bg-rozops-blue-light text-rozops-blue text-sm px-3 py-1.5 font-semibold">{e}</button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => {
            const showDate = i === 0 || dateKey(messages[i - 1]?.timestamp) !== dateKey(m.timestamp);
            return (
              <React.Fragment key={i}>
                {showDate && m.timestamp && (
                  <div className="flex justify-center">
                    <span className="rounded-full bg-white border border-gray-100 px-3 py-1 text-[11px] font-bold text-rozops-text-muted shadow-sm">{dateLabel(m.timestamp)}</span>
                  </div>
                )}
                <div className={`flex ${m.who === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${m.who === 'user' ? 'bg-rozops-blue text-white' : 'bg-rozops-bg text-rozops-text border border-gray-100'}`}>
                    {m.text}
                    {m.voice && <div className="mt-1 text-[11px] opacity-70">{m.who === 'user' ? 'Voice transcript' : 'AI voice answer'}</div>}
                    {m.history && <div className="mt-1 text-[11px] opacity-60">{m.timestamp}</div>}
                    {m.history && m.voice && m.who === 'ai' && !m.audioUrl && <div className="mt-1 text-[11px] opacity-60">Historical audio is not stored.</div>}
                    <ChatDataPreview payload={m.payload} />
                    {m.audioUrl && (
                      <button type="button" onClick={() => playVoice(m.audioUrl)} className="mt-2 rounded-lg bg-white/80 border border-gray-200 px-2.5 py-1.5 text-xs font-bold inline-flex items-center gap-1.5 text-rozops-blue">
                        <Volume2 className="w-3.5 h-3.5" /> Replay
                      </button>
                    )}
                    {m.payload && m.payload.intent && m.payload.intent !== 'unknown' && m.payload.requires_confirmation && m.canConfirm !== false && (
                      <div className="mt-2 pt-2 border-t border-gray-200/60">
                        <div className="text-xs opacity-70">Intent: <b>{m.payload.intent}</b></div>
                        <button onClick={() => apply(m)} className="mt-2 bg-rozops-green text-white rounded-lg px-3 py-1.5 text-xs font-bold" data-testid="ai-confirm">Confirm & apply</button>
                      </div>
                    )}
                  </div>
                </div>
              </React.Fragment>
            );
          })}
          {busy && <div className="text-rozops-text-muted text-sm inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Thinking…</div>}
          {voiceState === 'recording' && <div className="text-red-500 text-sm inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" /> Recording… tap stop when done.</div>}
          {voiceState === 'processing' && <div className="text-rozops-text-muted text-sm inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Transcribing and preparing voice answer…</div>}
          {voiceError && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2">{voiceError}</div>}
        </div>
      </div>

      <form onSubmit={send} className="flex gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)} disabled={voiceBusy} placeholder="Ask anything…" data-testid="ai-input" className="flex-1 bg-white border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-rozops-blue/20 focus:border-rozops-blue disabled:opacity-60" />
        <button
          type="button"
          onClick={voiceState === 'recording' ? stopRecording : startRecording}
          disabled={busy || voiceState === 'processing'}
          data-testid="ai-voice"
          title={voiceState === 'recording' ? 'Stop recording' : 'Record voice'}
          className={`rounded-xl px-4 py-3 font-semibold disabled:opacity-50 inline-flex items-center gap-2 ${voiceState === 'recording' ? 'bg-red-500 text-white' : 'bg-rozops-accent text-white'}`}
        >
          {voiceState === 'recording' ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
        </button>
        <button disabled={busy || voiceBusy} data-testid="ai-send" className="bg-rozops-blue text-white rounded-xl px-4 py-3 font-semibold disabled:opacity-50 inline-flex items-center gap-2"><Send className="w-4 h-4" /> Send</button>
      </form>
      </>
      )}

      {activeTab === 'summary' && (
        <Panel title="AI Business Summary">
          <div className="grid sm:grid-cols-2 xl:grid-cols-5 gap-3">
            {(insights.summary?.cards || []).map(card => (
              <div key={card.type} className="rounded-xl border border-gray-100 bg-rozops-bg p-4">
                <div className="flex items-center justify-between gap-2"><div className="text-xs uppercase tracking-wider text-rozops-text-muted font-bold">{card.title}</div><Risk value={card.risk} /></div>
                <div className="mt-2 font-heading font-extrabold text-xl text-rozops-navy">{typeof card.value === 'number' && card.type.includes('revenue') ? `₹${card.value.toLocaleString('en-IN')}` : card.value}</div>
                <div className="mt-1 text-xs text-rozops-text-muted">{card.detail}</div>
              </div>
            ))}
          </div>
          {!insights.summary?.cards?.length && <Empty text="No AI summary yet." />}
        </Panel>
      )}

      {activeTab === 'forecast' && (
        <Panel title="Demand Forecast" icon={TrendingUp}>
          <div className="grid lg:grid-cols-2 gap-4">
            <div>
              <div className="text-sm font-bold text-rozops-navy mb-2">7-day product requirement</div>
              {(insights.forecast?.totals || []).map(item => (
                <div key={item.product} className="flex justify-between border-b border-gray-100 py-2 text-sm"><span>{item.product}</span><b>{item.quantity}</b></div>
              ))}
              {!insights.forecast?.totals?.length && <Empty text="No active subscriptions available for forecast." />}
            </div>
            <div>
              <div className="text-sm font-bold text-rozops-navy mb-2">Daily plan</div>
              {(insights.forecast?.daily || []).map(day => (
                <div key={day.date} className="border-b border-gray-100 py-2 text-sm">
                  <div className="font-semibold text-rozops-navy">{day.date}</div>
                  <div className="text-rozops-text-muted">{day.products.map(p => `${p.product}: ${p.quantity}`).join(', ') || 'No demand'}</div>
                </div>
              ))}
            </div>
          </div>
        </Panel>
      )}

      {activeTab === 'billing' && (
        <Panel title="Billing Audit" icon={AlertTriangle}>
          {(insights.billing?.issues || []).map((issue, idx) => (
            <div key={idx} className="rounded-xl border border-gray-100 p-4 mb-3">
              <div className="flex items-center justify-between"><b className="text-rozops-navy">{issue.type}</b><Risk value={issue.severity} /></div>
              <div className="text-sm text-rozops-text-muted mt-1">{issue.message}</div>
            </div>
          ))}
          {!insights.billing?.issues?.length && <Empty text="No billing audit issues found." />}
        </Panel>
      )}

      {activeTab === 'payments' && (
        <Panel title="Payment Assistant" icon={IndianRupee}>
          {(insights.payments?.customers || []).map(row => (
            <div key={row.invoice_id} className="grid sm:grid-cols-5 gap-2 border-b border-gray-100 py-3 text-sm">
              <div className="font-semibold text-rozops-navy">{row.customer || 'Customer'}</div>
              <div>{row.invoice_number}</div>
              <div>₹{row.due_amount?.toLocaleString('en-IN')}</div>
              <div>{row.overdue_days} overdue day(s)</div>
              <Risk value={row.risk} />
            </div>
          ))}
          {!insights.payments?.customers?.length && <Empty text="No pending payments found." />}
        </Panel>
      )}

      {activeTab === 'customers' && (
        <Panel title="Customer Insights">
          <div className="grid lg:grid-cols-2 gap-4">
            <div>
              <div className="font-bold text-rozops-navy mb-2">High-value customers</div>
              {(insights.customers?.high_value_customers || []).map(c => <div key={c.id} className="flex justify-between border-b border-gray-100 py-2 text-sm"><span>{c.full_name}</span><b>₹{Math.round(c.total_billed || 0).toLocaleString('en-IN')}</b></div>)}
            </div>
            <div>
              <div className="font-bold text-rozops-navy mb-2">Needs attention</div>
              {(insights.customers?.churn_risk_customers || []).map(c => <div key={c.id} className="flex justify-between border-b border-gray-100 py-2 text-sm"><span>{c.full_name}</span><b>{c.issue_count} issue(s)</b></div>)}
            </div>
          </div>
        </Panel>
      )}

      {activeTab === 'routes' && (
        <Panel title="Route Insights" icon={Route}>
          {(insights.routes?.routes || []).map(route => (
            <div key={route.route_id || route.route_name} className="grid sm:grid-cols-4 gap-2 border-b border-gray-100 py-3 text-sm">
              <div className="font-semibold text-rozops-navy">{route.route_name}</div>
              <div>{route.area || 'No area'}</div>
              <div>{route.completed} / {route.planned} complete</div>
              <div>{route.issues} issue(s)</div>
            </div>
          ))}
          {!insights.routes?.routes?.length && <Empty text="No route deliveries found for today." />}
        </Panel>
      )}

      {activeTab === 'inventory' && (
        <Panel title="Inventory Insights" icon={PackageSearch}>
          {(insights.inventory?.recommendations || []).map(rec => (
            <div key={rec.inventory_item_id} className="grid sm:grid-cols-4 gap-2 border-b border-gray-100 py-3 text-sm">
              <div className="font-semibold text-rozops-navy">{rec.product}</div>
              <div>Stock: {rec.current_stock}</div>
              <div>Required: {rec.required}</div>
              <div className="font-bold text-rozops-green">Buy: {rec.suggested_purchase}</div>
            </div>
          ))}
          {!insights.inventory?.recommendations?.length && <Empty text={insights.inventory?.summary || 'No inventory recommendations.'} />}
        </Panel>
      )}

      {activeTab === 'audit' && (
        <Panel title="AI Audit Trail" icon={ShieldCheck}>
          <div className="grid lg:grid-cols-2 gap-5">
            <div>
              <div className="font-bold text-rozops-navy mb-2">Recent interpretations</div>
              {(insights.history || []).map(row => (
                <div key={row.id} className="rounded-xl border border-gray-100 p-4 mb-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <b className="text-sm text-rozops-navy">{row.detected_intent || 'unknown'}</b>
                    <span className="text-xs text-rozops-text-muted">{row.created_at ? new Date(row.created_at).toLocaleString() : ''}</span>
                  </div>
                  <div className="text-sm text-rozops-text-muted mt-1">{row.input_text}</div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <span className="rounded-full bg-rozops-bg px-2 py-1 text-[11px] font-bold text-rozops-text-muted">{row.status || 'logged'}</span>
                    {row.action_key && <span className="rounded-full bg-rozops-blue-light px-2 py-1 text-[11px] font-bold text-rozops-blue">{row.action_key}</span>}
                  </div>
                </div>
              ))}
              {!insights.history?.length && <Empty text="No AI interpretations have been logged yet." />}
            </div>
            <div>
              <div className="font-bold text-rozops-navy mb-2">Confirmed AI mutations</div>
              {(insights.auditLog || []).map(row => (
                <div key={row.id} className="rounded-xl border border-gray-100 p-4 mb-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <b className="text-sm text-rozops-navy">{row.action}</b>
                    <span className="text-xs text-rozops-text-muted">{row.created_at ? new Date(row.created_at).toLocaleString() : ''}</span>
                  </div>
                  <div className="text-sm text-rozops-text-muted mt-1">{row.entity_type || 'record'} {row.entity_id ? `#${String(row.entity_id).slice(0, 8)}` : ''}</div>
                  {row.new_value?.message && <div className="mt-2 text-sm text-rozops-text">{row.new_value.message}</div>}
                </div>
              ))}
              {!insights.auditLog?.length && <Empty text="No confirmed AI data changes yet." />}
            </div>
          </div>
        </Panel>
      )}
    </div>
  );
}
